import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Mic, ArrowLeft, ShieldCheck, ExternalLink, Plus, Network, MoreVertical, Trash2, Briefcase } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { RecommendationPill } from "../components/Scorecards/Scorecards";
import ChatPill from "../components/ChatPill";
import CandidateMindmap from "../components/CandidateMindmap";
import RecordingFlowController from "../components/Recording/RecordingFlowController";
import CandidateCV from "../components/CandidateCV";
import EditableTitle from "../components/EditableTitle";
import { AddToRoleDialog } from "../components/PipelineAssociationDialogs";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const STROKE = 1.5;

const STATUS_STYLE = {
  scheduled: "bg-ink-100 text-ink-700 border-ink-200",
  recording: "bg-amber-50 text-amber-800 border-amber-200",
  transcribed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-800/15",
};

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [scorecardsByInterview, setScorecardsByInterview] = useState({});
  const [recordOpen, setRecordOpen] = useState(false);
  const [mindmapOpen, setMindmapOpen] = useState(false);
  const [addToRoleOpen, setAddToRoleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isAdmin = member?.role === "admin";
  const canManagePipeline = ["recruiter", "admin"].includes(member?.role);
  const isFreeSeat = member?.role === "hiring_manager" || member?.role === "viewer";

  const reload = () => {
    api.get(`/candidates/${candidateId}/timeline`)
      .then(({ data }) => setData(data))
      .catch(() => {});
  };

  const deleteCandidate = async () => {
    const name = (data?.candidate?.name || "").trim();
    if (deleting || deleteText.trim() !== name) return;
    setDeleting(true);
    try {
      await api.delete(`/candidates/${candidateId}`);
      toast.success("Candidate permanently deleted");
      navigate("/candidates");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  useEffect(() => {
    api
      .get(`/candidates/${candidateId}/timeline`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load candidate"))
      .finally(() => setLoading(false));
    // Phase 4a — fetch scorecard pills per interview (parallel, independent of timeline)
    api
      .get(`/candidates/${candidateId}/scorecards/summary`)
      .then(({ data }) => {
        const byInterview = {};
        for (const row of data) {
          byInterview[row.interview_id] = row.recommendations || [];
        }
        setScorecardsByInterview(byInterview);
      })
      .catch(() => { /* viewer or no scorecards — silent */ });
  }, [candidateId]);

  if (loading) {
    return <AppShell><div className="flex justify-center p-12"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div></AppShell>;
  }

  if (err || !data) {
    return <AppShell><div className="hp-card p-6 text-red-700" data-testid="candidate-error">{err}</div></AppShell>;
  }

  const { candidate, timeline } = data;
  return (
    <AppShell>
      <div data-testid="candidate-detail-page" className="space-y-8">
        <header>
          <button onClick={() => navigate("/candidates")} className="inline-flex items-center gap-1 text-[13px] text-ink-500 hover:text-ink-900" data-testid="back-to-candidates">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={STROKE} /> All candidates
          </button>
          <h1 className="font-display mt-3 text-3xl font-medium tracking-tight text-ink-900">
            <EditableTitle
              value={candidate.name}
              canEdit={canManagePipeline}
              testid="candidate-name"
              placeholder="Untitled candidate"
              onSave={async (next) => {
                const { data: updated } = await api.patch(`/candidates/${candidate.candidate_id}`, { name: next });
                setData((prev) => ({ ...prev, candidate: { ...prev.candidate, ...updated } }));
                toast.success("Candidate renamed");
              }}
            />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[13px] text-ink-600">
            {candidate.email && <span>{candidate.email}</span>}
            {candidate.linkedin_url && (
              <a href={candidate.linkedin_url} target="_blank" rel="noreferrer" className="hp-link inline-flex items-center gap-1">
                LinkedIn <ExternalLink className="h-3 w-3" strokeWidth={STROKE} />
              </a>
            )}
            <span className="text-ink-400">·</span>
            <span className="text-ink-500">Added {new Date(candidate.created_at).toLocaleDateString()}</span>
          </div>
          {(canManagePipeline || isAdmin) && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="candidate-actions">
              {canManagePipeline && (
                <button
                  onClick={() => setAddToRoleOpen(true)}
                  className="inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-700 hover:border-emerald-800/40 hover:text-emerald-900"
                  data-testid="candidate-add-to-role"
                >
                  <Briefcase className="h-3.5 w-3.5" strokeWidth={STROKE} />
                  Add to role
                </button>
              )}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="inline-flex items-center justify-center rounded-md border border-ink-200 bg-white p-1.5 text-ink-600 hover:bg-ink-50"
                      data-testid="candidate-overflow-menu"
                      aria-label="More actions"
                    >
                      <MoreVertical className="h-4 w-4" strokeWidth={STROKE} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onClick={() => { setDeleteText(""); setDeleteOpen(true); }}
                      className="text-red-600 focus:text-red-700"
                      data-testid="delete-candidate-menu-item"
                    >
                      <Trash2 className="mr-2 h-4 w-4" strokeWidth={STROKE} />
                      Delete candidate
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
          {timeline.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => setRecordOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900"
                data-testid="candidate-add-interview"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Add interview
              </button>
              <button
                onClick={() => setMindmapOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[13px] font-medium text-ink-700 hover:border-emerald-800/40 hover:text-emerald-900"
                data-testid="candidate-mindmap-button"
              >
                <Network className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Topic map
              </button>
            </div>
          )}
          {timeline.length === 0 && (
            <div className="mt-4">
              <button
                onClick={() => setRecordOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-900"
                data-testid="candidate-record-first"
              >
                <Mic className="h-3.5 w-3.5" strokeWidth={STROKE} />
                Record first interview
              </button>
            </div>
          )}
        </header>

        <CandidateCV candidateId={candidateId} />

        <section>
          <div className="hp-section-label mb-3">Interview timeline · {timeline.length} round{timeline.length === 1 ? "" : "s"}</div>
          {timeline.length === 0 && (
            <div className="hp-card flex flex-col items-center justify-center p-12 text-center" data-testid="candidate-timeline-empty">
              <Mic className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
              <p className="mt-3 text-[14px] text-ink-500">No interviews recorded for {candidate.name} yet.</p>
            </div>
          )}
          {timeline.length > 0 && (
            <div className="relative pl-6" data-testid="candidate-timeline">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-ink-200" />
              {timeline.map((entry) => {
                const i = entry.interview;
                return (
                  <div key={i.interview_id} className="relative mb-6 last:mb-0" data-testid={`timeline-entry-${i.interview_id}`}>
                    <span className="absolute -left-[18px] top-2 h-3 w-3 rounded-full bg-emerald-800 ring-4 ring-ink-50" />
                    <Link to={`/interviews/${i.interview_id}`} className="hp-card block p-5 transition hover:border-emerald-800/30 hover:bg-emerald-50/30">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-[12px] font-medium uppercase tracking-wider text-ink-500">Round {i.round_number} · {i.type}</div>
                          <h3 className="font-display mt-1 text-lg font-medium text-ink-900">{i.title || "Untitled interview"}</h3>
                          {entry.role?.title && <div className="mt-0.5 text-[13px] text-ink-600">for {entry.role.title}</div>}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[i.status] || "bg-ink-100 text-ink-700 border-ink-200"}`}>
                            {i.status}
                          </span>
                          {i.consent_obtained && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-800">
                              <ShieldCheck className="h-3 w-3" strokeWidth={STROKE} /> Consent
                            </span>
                          )}
                        </div>
                      </div>
                      {entry.summary_preview && (
                        <p className="mt-3 text-[14px] leading-relaxed text-ink-700" data-testid={`timeline-summary-${i.interview_id}`}>
                          {entry.summary_preview}
                        </p>
                      )}
                      {/* Phase 4a — inline scorecard pills (NOT pre-aggregated) */}
                      {scorecardsByInterview[i.interview_id]?.length > 0 && (
                        <div
                          className="mt-3 flex flex-wrap gap-1.5 items-center"
                          data-testid={`timeline-recommendations-${i.interview_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[11px] text-ink-500 uppercase tracking-wider font-medium">
                            {scorecardsByInterview[i.interview_id].length === 1 ? "Recommendation" : `Recommendations · ${scorecardsByInterview[i.interview_id].length}`}
                          </span>
                          {scorecardsByInterview[i.interview_id].slice(0, 4).map((rec) => (
                            <RecommendationPill
                              key={rec.scorecard_id}
                              recommendation={rec.recommendation}
                              status={rec.status}
                              fillerName={rec.filled_by_name}
                              tooltipExtra={rec.submitted_at
                                ? new Date(rec.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                : null}
                            />
                          ))}
                          {scorecardsByInterview[i.interview_id].length > 4 && (
                            <span
                              className="inline-flex items-center rounded-md border border-ink-200 bg-white px-2 py-0.5 text-[11px] font-medium text-ink-600"
                              title={scorecardsByInterview[i.interview_id].slice(4).map(r => `${r.filled_by_name} · ${r.recommendation}`).join("\n")}
                            >
                              +{scorecardsByInterview[i.interview_id].length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                      {entry.key_topics?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {entry.key_topics.map((t) => (
                            <span key={t} className="rounded-md border border-ink-200 bg-ink-50 px-2 py-0.5 text-[11px] text-ink-700">{t}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 text-[12px] text-ink-400">{new Date(i.created_at).toLocaleString()}</div>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
      {/* Persistent bottom-right pill — only renders when there's at least
          one round to chat over. ChatPill mounts its own CandidateChatPanel
          internally. */}
      {timeline.length > 0 && !isFreeSeat && (
        <ChatPill candidateId={candidateId} />
      )}
      <CandidateMindmap
        candidateId={candidateId}
        candidateName={candidate.name}
        open={mindmapOpen}
        onClose={() => setMindmapOpen(false)}
      />
      <RecordingFlowController
        open={recordOpen}
        onOpenChange={(v) => {
          setRecordOpen(v);
          // Re-fetch the candidate after the dialog closes so a new round
          // shows up immediately on the timeline + the chat panel sees it.
          if (!v) {
            api.get(`/candidates/${candidateId}/timeline`)
              .then(({ data }) => setData(data))
              .catch(() => {});
          }
        }}
        prefill={candidate ? {
          candidate_id: candidate.candidate_id,
          candidate_name: candidate.name,
          round_count: timeline.length,
        } : null}
      />
      <AddToRoleDialog
        open={addToRoleOpen}
        onOpenChange={setAddToRoleOpen}
        candidateId={candidateId}
        onAdded={reload}
      />
      <AlertDialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>
        <AlertDialogContent data-testid="delete-candidate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl font-medium">Delete this candidate permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-ink-600">
              Permanently deletes this candidate and all interviews, recordings, transcripts,
              scorecards, CV, chat history, and derived data. This cannot be undone. Files
              already synced to Google Drive are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 text-[13px] text-ink-700">
            <p>Type <strong>{candidate.name}</strong> (the candidate&rsquo;s name) to confirm:</p>
            <input
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] focus:border-red-400 focus:outline-none"
              placeholder={candidate.name}
              data-testid="delete-candidate-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-candidate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteCandidate(); }}
              disabled={deleting || deleteText.trim() !== (candidate.name || "").trim()}
              className="bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="delete-candidate-confirm-button"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
